/**
 * @file WebSocket subscription contract for `@labcraft/mentor` — the
 *       single typed channel between a lab session and the mentor
 *       daemon.
 * @module @labcraft/mentor/shared/subscriptions
 *
 * The mentor is **course-agnostic**: nothing on this wire names a
 * course, chapter, or content graph. The lab (the caller) flattens
 * whatever it knows about the learner's current exercise into one
 * `context` frame — an opaque `objectiveId` key, a human-readable
 * objective description, the authored mentoring payload
 * (`MentorContextSchema`), and (post-#1417) an opaque static-context
 * block (T1). The mentor never joins that key against any structure; it
 * only ladders per `(session, objectiveId)` and forwards the injected
 * content as opaque text (content-carrying, not content-parsing).
 *
 * `MentorContextSchema` mirrors course-kit's `MentorContext` shape
 * (`{ rationale, misconceptions, guidance? }`) but is declared here in
 * the wire layer on purpose — importing course-kit would couple the
 * daemon to course structure, which is exactly what umbrella #1139 §3
 * forbids. The two schemas agreeing is a lab-side (caller) concern.
 *
 * ## Capture-ownership seam (umbrella #1417)
 *
 * The mentor is DB-less and identity-blind — its session key is
 * `vm:<id>:shell:<x>`, never a learner id — so it CANNOT own durable
 * capture: it has no learner key to root the #1094 `ON DELETE CASCADE`
 * on. Therefore the **lab** owns the two capture tables
 * (`mentor_interactions`, `mentor_wrong_turns`; see umbrella Phase 6),
 * and the mentor owns exactly one thing: its **per-observation
 * telemetry** (disposition, nullable diagnosis, token counts), which it
 * **emits on the `observed` frame and never persists**. Everything the
 * lab can't derive itself rides that outbound frame, correlated to the
 * observation by `correlationId` so the lab stitches one row.
 *
 * ## Two-region request (umbrella #1457, Lane B)
 *
 * The tiered prompt-cache (#1417) never fired — the stable prefix sat
 * under Sonnet 5's 1024-token minimum cacheable prefix — so it was
 * removed. The mentor request is now an uncached two-region shape:
 * authored mentoring context (objective + rationale + misconceptions +
 * guidance) rides the **system** region; the observation and the
 * learner's recent command window (`observation.history`) ride the
 * **user turn**. The authored context arrives structured on the
 * `context` frame; the mentor serializes it into the system region
 * itself (HTML-normalised) rather than parsing course semantics.
 * `observation.history` is an append-only recent-command window the lab
 * assembles and the mentor forwards verbatim into the user turn.
 *
 * Path is wire-true and starts with the daemon's `id` prefix
 * (`/mentor`), per `.agents/rules/services.md`.
 */

import { z } from 'zod'
import { defineSubscription } from '@labcraft/core'

////////////////////////////////////////////////////////////////////////////////
// Authored mentoring context
////////////////////////////////////////////////////////////////////////////////

/**
 * One authored misconception for an objective — a known wrong turn keyed
 * to the `WrongTurn.id` that detects it (#1566). Keying the misconception
 * to its wrong-turn id lets the mentor resolve a fired wrong turn to its
 * exact authored hook — needed for the hook-budget gate (no model call
 * while a hook holds) and to avoid parroting a hook the learner already
 * saw.
 *
 * @property wrongTurnId - The authored `WrongTurn.id` this misconception
 *   detects; joins a fired `wrongTurn` frame to its hook text.
 * @property hook - The author's words for the wrong turn (the former bare
 *   misconception string; also the browser-side `mentorHook`).
 */
export const MentorMisconceptionSchema = z.object({
  wrongTurnId: z.string().min(1),
  hook: z.string(),
})

/** Parsed `MentorMisconception`. */
export type MentorMisconception = z.infer<typeof MentorMisconceptionSchema>

/**
 * Authored mentoring payload for one objective — the course author's
 * notes on why the objective matters and where learners go wrong.
 * Shape mirrors course-kit's `MentorContext` but is declared here (see
 * file header for why the wire layer owns its own copy).
 *
 * @property rationale - Why this objective exists; what the learner is
 *   supposed to internalize. Feeds the model's judgment of whether an
 *   observation is off-track at all.
 * @property misconceptions - Known wrong turns learners take on this
 *   objective, each keyed to the `WrongTurn.id` that detects it (#1566).
 *   The model checks the observation against these before anything else,
 *   and resolves a reply's `[M<n>]` tag against them.
 * @property guidance - Optional author hint on how to steer without
 *   giving the answer (an analogy to use, a concept to point at).
 */
export const MentorContextSchema = z.object({
  rationale: z.string(),
  misconceptions: z.array(MentorMisconceptionSchema),
  guidance: z.string().optional(),
})

/** Parsed `MentorContext`. */
export type MentorContext = z.infer<typeof MentorContextSchema>

////////////////////////////////////////////////////////////////////////////////
// Client → server frames
////////////////////////////////////////////////////////////////////////////////

/**
 * Context frame — the lab sets (or replaces) the learner's active
 * objective for this session. Observations arriving before any context
 * frame are ignored (the mentor has nothing to judge them against).
 * Re-sending a context frame with a new `objectiveId` switches the
 * active objective without touching other objectives' ladder rungs —
 * the restraint ladder is tracked per `(session, objectiveId)`.
 *
 * @property type - Literal `'context'` (discriminant).
 * @property objectiveId - Opaque key for the active objective. The
 *   mentor never parses it; it only scopes the restraint ladder.
 * @property description - Human-readable statement of what the learner
 *   is trying to accomplish. Serialized into the system region (umbrella
 *   #1457), HTML-normalised, alongside the authored mentoring context.
 * @property mentor - The authored mentoring payload for this objective.
 *   The mentor serializes it into the system region (objective +
 *   rationale + numbered misconceptions + guidance) and also reads
 *   `misconceptions` server-side to resolve a reply's `[M<n>]` tag.
 */
export const MentorContextFrameSchema = z.object({
  type: z.literal('context'),
  objectiveId: z.string().min(1),
  description: z.string(),
  mentor: MentorContextSchema,
})

/** Parsed `MentorContextFrame`. */
export type MentorContextFrame = z.infer<typeof MentorContextFrameSchema>

/**
 * Observation frame — one completed command and its result, as
 * captured by the lab's command-event pipeline (OSC 133). This is the
 * mentor's unit of judgment: each observation triggers (at most) one
 * model call deciding nudge-vs-silence, and (post-#1417) exactly one
 * outbound `observed` telemetry frame carrying its `correlationId`.
 *
 * @property type - Literal `'observation'` (discriminant).
 * @property correlationId - Lab-minted id unique to this observation
 *   (umbrella #1417). The mentor echoes it back on the `observed` frame
 *   so the lab stitches one capture row per observation.
 * @property cmd - The command line the learner ran.
 * @property exit - The command's exit status.
 * @property cwd - Working directory the command ran in.
 * @property output - Captured command output. The lab bounds this
 *   before sending; the daemon re-truncates to its configured
 *   `maxOutputBytes` as defence-in-depth.
 * @property history - Optional recent-command window (umbrella #1457):
 *   an opaque, append-only window of the learner's recent VM commands
 *   the lab assembles; the mentor forwards it verbatim into the model's
 *   user turn alongside this observation. Absent when the lab has no
 *   prior commands to include.
 */
export const MentorObservationFrameSchema = z.object({
  type: z.literal('observation'),
  correlationId: z.string().min(1),
  cmd: z.string(),
  exit: z.number().int(),
  cwd: z.string(),
  output: z.string(),
  history: z.string().optional(),
})

/** Parsed `MentorObservationFrame`. */
export type MentorObservationFrame = z.infer<
  typeof MentorObservationFrameSchema
>

/**
 * Progress/recovery frame (umbrella #1417, extended by #1566) — the lab
 * tells the mentor the active objective advanced or was satisfied. The
 * mentor has no completion visibility of its own; this is the signal the
 * adaptive-help part (umbrella Phase 5) consumes to FADE help on progress
 * (no longer monotonic) and the lab's capture derives the `RECOVERED`
 * outcome from. Mostly a pure inbound signal (no model call, no outbound
 * frame); the #1566 exception is a `satisfied` frame carrying `encourage`,
 * which fires one `encouragement` observed frame (see below).
 *
 * @property type - Literal `'progress'` (discriminant).
 * @property objectiveId - The objective the progress is scoped to; same
 *   opaque key space as the `context` frame's.
 * @property kind - `'advanced'` = the learner made forward progress
 *   (fade one level); `'satisfied'` = the objective's completion
 *   detector fired (the recovery signal).
 * @property encourage - #1566: set by the lab from the operator's
 *   `mentorEncouragementEnabled` setting on a `satisfied` frame. When
 *   `true` and the session holds struggle history, the mentor spends a
 *   cheap model call and emits an `encouragement` frame. The mentor stays
 *   stateless about the setting — the flag rides the frame. Absent /
 *   `false` → fade only (the pre-#1566 behaviour).
 * @property correlationId - #1566: present with `encourage`; lets the lab
 *   stitch the encouragement capture row (the `observed` frame echoes it).
 * @property cmd - #1566: the command that satisfied the objective, so the
 *   encouragement can name why that approach worked. Present with
 *   `encourage`.
 */
export const MentorProgressFrameSchema = z.object({
  type: z.literal('progress'),
  objectiveId: z.string().min(1),
  kind: z.enum(['advanced', 'satisfied']),
  encourage: z.boolean().optional(),
  correlationId: z.string().min(1).optional(),
  cmd: z.string().optional(),
})

/** Parsed `MentorProgressFrame`. */
export type MentorProgressFrame = z.infer<typeof MentorProgressFrameSchema>

/**
 * Wrong-turn frame (#1150 + #1566) — one deterministic Tier-1 wrong turn
 * fired browser-side (course-kit's `rethink` dispatch), relayed up by the
 * lab. The lab durably captures it in `mentor_wrong_turns`; the mentor
 * uses it to arm the #1566 hook-budget gate — it resolves the fired
 * `wrongTurnId` to its authored hook via the keyed context and holds off
 * any model call while the hook is within its attempt budget (disposition
 * `held`). The mentor still neither aggregates the firing nor replies to
 * it (the in-memory flywheel was retired in umbrella #1417 part #1432) —
 * the authored `mentorHook` already surfaced browser-side, zero-token.
 * The durable count is the retirement / re-validation signal for promoted
 * (and authored) rules: a rule that stops firing is a retirement
 * candidate; one that keeps firing is re-validated.
 *
 * @property type - Literal `'wrongTurn'` (discriminant).
 * @property objectiveId - The objective/exercise the wrong turn is
 *   scoped to. Opaque, same key space as the `context` frame's.
 * @property wrongTurnId - The authored `WrongTurn.id` that fired.
 */
export const MentorWrongTurnFrameSchema = z.object({
  type: z.literal('wrongTurn'),
  objectiveId: z.string().min(1),
  wrongTurnId: z.string().min(1),
})

/** Parsed `MentorWrongTurnFrame`. */
export type MentorWrongTurnFrame = z.infer<typeof MentorWrongTurnFrameSchema>

/** Client → server frames for the mentor channel. */
export const MentorClientFrameSchema = z.discriminatedUnion('type', [
  MentorContextFrameSchema,
  MentorObservationFrameSchema,
  MentorProgressFrameSchema,
  MentorWrongTurnFrameSchema,
])

/** Parsed `MentorClientFrame`. */
export type MentorClientFrame = z.infer<typeof MentorClientFrameSchema>

////////////////////////////////////////////////////////////////////////////////
// Server → client frames
////////////////////////////////////////////////////////////////////////////////

/**
 * Restraint-ladder rung. 1 = nudge (orienting question), 2 = reframe
 * (point at the concept), 3 = shape (structure of the approach). The
 * ladder never exceeds 3 — the ceiling (never paste the runnable
 * command, never reveal the full solution) holds at every rung. This is
 * the mentor's INTERNAL ladder index (still monotonic until the
 * adaptive-help part, umbrella Phase 5); the wire-facing successor is
 * `MentorHelpLevel` on the `observed` frame.
 */
export const MentorRungSchema = z.union([
  z.literal(1),
  z.literal(2),
  z.literal(3),
])

/** Parsed `MentorRung`. */
export type MentorRung = z.infer<typeof MentorRungSchema>

/**
 * Wire-facing help level on the `observed` frame (umbrella #1417) — the
 * successor to the monotonic `rung`. Same 1..3 escalation vocabulary;
 * the adaptive-help part (umbrella Phase 5) makes it non-monotonic
 * (escalates within an error class on unproductive struggle, de-escalates
 * on progress). Until then it tracks the internal ladder rung 1:1.
 */
export const MentorHelpLevelSchema = z.union([
  z.literal(1),
  z.literal(2),
  z.literal(3),
])

/** Parsed `MentorHelpLevel`. */
export type MentorHelpLevel = z.infer<typeof MentorHelpLevelSchema>

/**
 * Disposition of one processed observation (umbrella #1417, extended by
 * the #1566 cost ladder). `nudge` = the model ran and generated help;
 * `silent` = the model ran and chose not to nudge — and, as an interim
 * compromise (#1563), also a `max_tokens` truncation, where the model ran
 * but produced no usable verdict (counted separately on
 * `observe.truncated_total`; a dedicated `truncated` disposition is
 * deferred); `held` = an authored wrong-turn hook is within its attempt
 * budget so the mentor made NO model call at all (#1566 — distinct from
 * `silent`, where a model DID run, and from `budget_exceeded`, a
 * session-budget trip); `encouragement` = the learner recovered after a
 * struggle and a model call generated success reinforcement + why it
 * worked (#1566; text-bearing like `nudge`); `budget_exceeded` = a
 * per-session budget / debounce trip suppressed the call (NO model ran —
 * never conflated with `silent`); `error` = the model call failed. The
 * canonical outbound-telemetry enum the capture part keys its
 * `disposition` column on.
 */
export const MentorDispositionSchema = z.enum([
  'nudge',
  'silent',
  'held',
  'encouragement',
  'budget_exceeded',
  'error',
])

/** Parsed `MentorDisposition`. */
export type MentorDisposition = z.infer<typeof MentorDispositionSchema>

/**
 * Structured diagnosis the model produces alongside a nudge (umbrella
 * #1417). Frozen here; the adaptive-help part (umbrella Phase 5)
 * populates it — until then the frame omits `diagnosis`.
 *
 * @property errorClass - The model's classification of the error class
 *   (e.g. `'syntax'`, `'wrong-tool'`, `'conceptual'`).
 * @property misconception - The authored misconception text the struggle
 *   matched (verbatim), or `null` when untagged ("unknown unknowns").
 * @property productive - Whether the struggle is productive (the learner
 *   is converging) vs unproductive (looping / regressing).
 */
export const MentorDiagnosisSchema = z.object({
  errorClass: z.string(),
  misconception: z.string().nullable(),
  productive: z.boolean(),
})

/** Parsed `MentorDiagnosis`. */
export type MentorDiagnosis = z.infer<typeof MentorDiagnosisSchema>

/**
 * Per-run token accounting on the `observed` frame (umbrella #1417).
 * Mirrors `AgentResult.tokens` verbatim (camelCase) so the mentor
 * forwards its own result without a translation layer; the lab maps the
 * four counts to snake_case DB columns in the capture part.
 *
 * @property input - Uncached input tokens billed for the run.
 * @property output - Output tokens generated.
 * @property cacheRead - Prompt-cache read tokens (0 when the provider
 *   surfaces no cache metrics).
 * @property cacheWrite - Prompt-cache write tokens.
 */
export const MentorTokensSchema = z.object({
  input: z.number().int().nonnegative(),
  output: z.number().int().nonnegative(),
  cacheRead: z.number().int().nonnegative(),
  cacheWrite: z.number().int().nonnegative(),
})

/** Parsed `MentorTokens`. */
export type MentorTokens = z.infer<typeof MentorTokensSchema>

/**
 * Observed frame (umbrella #1417) — emitted for EVERY processed
 * observation (not just nudges), broadcast to every WS attached to the
 * session. Subsumes the old nudge-only frame: on a text-bearing
 * disposition it carries the learner-facing `text` (the SPA renders it);
 * a `nudge` additionally carries `helpLevel`, so one correlated frame
 * serves both SPA delivery and the lab's capture. Nullable-by-disposition:
 *
 * - `nudge` — `text` + `helpLevel` + `tokens` (+ `diagnosis` once the
 *   adaptive-help part lands).
 * - `encouragement` — `text` + `tokens` (#1566; success-after-struggle
 *   reinforcement); no `helpLevel` (it is not a restraint rung).
 * - `silent` — `tokens` (the model ran); no `text`/`helpLevel`. Also the
 *   disposition for a `max_tokens` truncation (interim compromise, #1563;
 *   the count lives on `observe.truncated_total`).
 * - `held` — none (#1566; an authored hook holds, no model ran).
 * - `budget_exceeded` — none (no model ran).
 * - `error` — none (the call failed).
 *
 * @property type - Literal `'observed'` (discriminant).
 * @property correlationId - Echoes the observation's `correlationId` so
 *   the lab stitches one capture row.
 * @property objectiveId - The objective the observation was judged
 *   against (the active context's key at call time).
 * @property disposition - What happened to the observation.
 * @property text - Learner-facing text (Socratic nudge, or #1566
 *   encouragement; never the answer). Present on any text-bearing
 *   disposition (`nudge` + `encouragement`).
 * @property helpLevel - The help level the nudge was generated at.
 *   Present iff `disposition === 'nudge'`.
 * @property diagnosis - Structured model diagnosis; present on `nudge`
 *   once the adaptive-help part populates it.
 * @property tokens - Per-run token counts; present when a model call ran
 *   (`nudge`/`silent`/`encouragement`).
 */
export const MentorObservedFrameSchema = z.object({
  type: z.literal('observed'),
  correlationId: z.string().min(1),
  objectiveId: z.string().min(1),
  disposition: MentorDispositionSchema,
  text: z.string().min(1).optional(),
  helpLevel: MentorHelpLevelSchema.optional(),
  diagnosis: MentorDiagnosisSchema.optional(),
  tokens: MentorTokensSchema.optional(),
})

/** Parsed `MentorObservedFrame`. */
export type MentorObservedFrame = z.infer<typeof MentorObservedFrameSchema>

/**
 * Server → client frames for the mentor channel. A single member today
 * (the per-observation `observed` telemetry frame); the discriminated
 * union keeps a future frame (e.g. a typing/thinking indicator) a
 * non-breaking addition.
 */
export const MentorServerFrameSchema = z.discriminatedUnion('type', [
  MentorObservedFrameSchema,
])

/** Parsed `MentorServerFrame`. */
export type MentorServerFrame = z.infer<typeof MentorServerFrameSchema>

////////////////////////////////////////////////////////////////////////////////
// Subscription
////////////////////////////////////////////////////////////////////////////////

/**
 * Path params for `MentorSub`. `sessionId` is the lab-minted session
 * key the in-memory mentor state (context + ladder) is keyed by; a
 * reconnect that dials the same `sessionId` reattaches to the same
 * state.
 */
export const MentorSubParamsSchema = z.object({
  sessionId: z.string().min(1),
})

/** Parsed `MentorSubParams`. */
export type MentorSubParams = z.infer<typeof MentorSubParamsSchema>

/**
 * `WS /mentor/:sessionId` — typed subscription for a lab session's
 * mentoring channel. The lab sends `context` (active objective +
 * authored mentoring payload + optional T1) then a stream of
 * `observation` frames (+ optional T2), `progress` (objective advanced /
 * satisfied), and `wrongTurn` (Tier-1 firing) frames; the mentor pushes
 * one `observed` telemetry frame per processed observation.
 */
export const MentorSub = defineSubscription({
  path: '/mentor/:sessionId',
  params: MentorSubParamsSchema,
  clientFrame: MentorClientFrameSchema,
  serverFrame: MentorServerFrameSchema,
  description:
    'Course-agnostic mentoring channel (#1146, #1417). Client sends ' +
    '`context` (active objective + authored mentor payload + optional T1 ' +
    'static content, opaque objectiveId), `observation` (one completed ' +
    'command + bounded output + optional T2 command history + ' +
    'correlationId), `progress` (objective advanced/satisfied), and ' +
    '`wrongTurn` (one Tier-1 firing — lab-captured telemetry, #1150) ' +
    'frames; ' +
    'the server pushes one `observed` telemetry frame per processed ' +
    'observation (disposition, nullable diagnosis/tokens, correlationId). ' +
    'State is in-memory per sessionId and survives reconnects until the ' +
    'idle TTL.',
})
